import React, { useState } from 'react'
import { encodeToBase64, isAcceptedFormat } from '../utils'
import { DEFAULT_FILE_SIZE } from '../constants'

/**
 * @typedef {object} FileData
 * @property {string} data - El archivo codificado en base64.
 * @property {string} name - El nombre original del archivo.
 * @property {string} type - La extensión del archivo (ej. 'pdf', 'jpg').
 */

/**
 * @typedef {object} FileChangeEvent
 * @property {object} target - Un objeto que simula la estructura de un evento de cambio nativo.
 * @property {string} target.name - El nombre del campo, proveniente de la prop `name`.
 * @property {FileData} target.value - Un objeto con los datos del archivo codificado en base64 y sus metadatos.
 */

/**
 * @typedef {object} FileUploadErrorData
 * @property {string} mssg - El mensaje de error descriptivo.
 */

/**
 * @typedef {object} RenderProps
 * @property {boolean} loading - Indica si el archivo se está procesando actualmente.
 * @property {string | false} fileName - El nombre del archivo seleccionado, o `false` si no hay archivo.
 */

/**
 * @typedef {object} FileUploaderAgnosticProps
 * @property {string} [idName] - ID personalizado para el input de archivo. Si no se proporciona, se usa `name`.
 * @property {string} name - El nombre del campo, que se incluirá en el objeto de evento sintético del `onSuccess`.
 * @property {string[]} accept - Array de extensiones de archivo permitidas (ej. `['.pdf', '.jpg', '.png']`).
 * @property {function(FileChangeEvent): void} onSuccess - Callback que se ejecuta cuando el archivo se procesa exitosamente. Recibe un evento sintético con los datos del archivo.
 * @property {function(FileUploadErrorData): void} onError - Callback que se ejecuta cuando ocurre un error. Recibe un objeto con el mensaje de error.
 * @property {boolean} [disabled=false] - Si es `true`, deshabilita la selección de archivos.
 * @property {number} [maxSize=DEFAULT_FILE_SIZE] - Tamaño máximo permitido del archivo en bytes.
 * @property {function(RenderProps): React.ReactNode} children - Función render prop que recibe el estado de carga y el nombre del archivo, y devuelve el elemento que actúa como trigger de selección.
 */

/**
 * Un componente de subida de archivos agnóstico que codifica archivos en base64 sin realizar peticiones HTTP.
 * 
 * A diferencia de `FileUploader`, este componente no envía archivos a un servidor. En su lugar, codifica
 * el archivo seleccionado en base64 y lo devuelve a través del callback `onSuccess`, permitiendo al
 * componente padre decidir qué hacer con los datos.
 * 
 * Características principales:
 * - **Validación de formato**: Verifica que el archivo tenga una extensión permitida
 * - **Validación de tamaño**: Comprueba que el archivo no exceda el tamaño máximo
 * - **Codificación base64**: Convierte el archivo a base64 de forma local
 * - **Render prop pattern**: Permite personalizar completamente la UI del botón/trigger
 * - **Sin dependencias de backend**: No realiza peticiones HTTP, ideal para formularios complejos
 * 
 * El componente utiliza el patrón render prop para máxima flexibilidad, permitiendo que el padre
 * controle completamente la apariencia del elemento que activa la selección de archivos.
 * 
 * @component
 * @example
 * const MyForm = () => {
 *   const [fileData, setFileData] = useState(null);
 * 
 *   const handleFileSelect = (e) => {
 *     setFileData(e.target.value);
 *     console.log('Archivo codificado:', e.target.value.data);
 *   };
 * 
 *   const handleError = (error) => {
 *     alert(error.mssg);
 *   };
 * 
 *   return (
 *     <FileUploaderAgnostic
 *       name="documento"
 *       accept={['.pdf', '.docx']}
 *       maxSize={10 * 1024 * 1024} // 10MB
 *       onSuccess={handleFileSelect}
 *       onError={handleError}
 *     >
 *       {({ loading, fileName }) => (
 *         <Button disabled={loading}>
 *           {loading ? 'Procesando...' : fileName || 'Seleccionar archivo'}
 *         </Button>
 *       )}
 *     </FileUploaderAgnostic>
 *   );
 * };
 * 
 * @param {FileUploaderAgnosticProps} props - Las props del componente FileUploaderAgnostic.
 */
export const FileUploaderAgnostic = ({
  idName,
  name,
  accept,
  onSuccess,
  onError,
  disabled = false,
  maxSize = DEFAULT_FILE_SIZE,
  children,
}) => {
  const [loading, setLoading] = useState(false)
  const [fileName, setFileName] = useState(false)

  const handleChangeUpload = async (e) => {
    setLoading(true)
    const file = e.target.files[0]
    if (file) {
      if (accept && accept.length > 0 && !isAcceptedFormat(file.name, accept)) {
        setLoading(false)
        return onError({ mssg: 'Archivo con formato no válido' })
      } else if (file.size > maxSize) {
        setLoading(false)
        return onError({ mssg: 'Excede el tamaño máximo' })
      } else {
        setFileName(file.name)
        const fileBase64 = await encodeToBase64(file, true)
        setLoading(false)
        return onSuccess({
          target: {
            name,
            value: { data: fileBase64, name: file.name, type: file.type.split('/')[1] },
          },
        })
      }
    }
    setLoading(false)
  }
  const nameId = `file-upload-${idName || name}`
  return (
    <div>
      <input
        id={nameId}
        name={name}
        type="file"
        style={{ display: 'none' }}
        accept={accept.join(',')}
        onChange={handleChangeUpload}
        disabled={disabled}
      />
      <label htmlFor={nameId}>{children({ loading, fileName })}</label>
    </div>
  )
}
